'use client'

import { useEffect, useState } from 'react'
import { useRouter } from 'next/navigation'
import { storage } from '@/utils/storage'
import { ROUTES, ROLES } from '@/config'

/**
 * Higher Order Component for protecting routes with authentication and role-based access
 * @param {React.Component} Component - The component to wrap
 * @param {string|string[]|null} allowedRoles - Role(s) allowed to access the route. null means any authenticated user
 * @param {React.Component} FallbackComponent - Optional component to show while checking auth or when unauthorized
 * @returns {React.Component} Protected component
 */
export function withAuth(
  Component,
  allowedRoles = null,
  FallbackComponent = null
) {
  return function AuthenticatedComponent(props) {
    const router = useRouter()
    const [isAuthorized, setIsAuthorized] = useState(false)
    const [isChecking, setIsChecking] = useState(true)
    const [user, setUser] = useState(null)

    useEffect(() => {
      const checkAuth = () => {
        const token = storage.getAccessToken()
        const currentUser = storage.getUser()

        // Check if user is authenticated
        if (!token || !currentUser) {
          setIsAuthorized(false)
          setIsChecking(false)
          // Redirect to login if not authenticated
          router.replace(ROUTES.LOGIN)
          return
        }

        // If no specific role required, any authenticated user can access
        if (allowedRoles === null) {
          setUser(currentUser)
          setIsAuthorized(true)
          setIsChecking(false)
          return
        }

        // Check if user has required role(s)
        const userRole = currentUser.role
        const rolesArray = Array.isArray(allowedRoles)
          ? allowedRoles
          : [allowedRoles]

        if (rolesArray.includes(userRole)) {
          setUser(currentUser)
          setIsAuthorized(true)
          setIsChecking(false)
        } else {
          setIsAuthorized(false)
          setIsChecking(false)
          // Redirect to not-found if wrong role
          router.replace(ROUTES.NOT_FOUND)
        }
      }

      checkAuth()
    }, [router, allowedRoles])

    // Show loading/fallback while checking
    if (isChecking) {
      if (FallbackComponent) {
        return <FallbackComponent />
      }
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      )
    }

    // Not authorized - show fallback or return null (redirect should have happened)
    if (!isAuthorized) {
      if (FallbackComponent) {
        return <FallbackComponent />
      }
      return null
    }

    // Authorized - render the component with user prop
    return <Component {...props} user={user} />
  }
}

/**
 * Helper function to get default route based on user role
 */
export function getDefaultRouteForRole(role) {
  switch (role) {
    case ROLES.ADMIN:
      return ROUTES.ADMIN_DASHBOARD
    case ROLES.LANDLORD:
      return ROUTES.LANDLORD_DASHBOARD
    case ROLES.TENANT:
      return ROUTES.TENANT_DASHBOARD
    default:
      return ROUTES.LOGIN
  }
}
